import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormRecord,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { Objection, ObjectionType } from '../../../core/objection/objection';
import { ObjectionService } from '../../../core/objection/objection.service';

@Component({
  selector: 'app-new-edit-objection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzListModule,
    NzRadioModule,
  ],
  templateUrl: './new-edit-objection.component.html',
  styleUrl: './new-edit-objection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewEditObjectionComponent {
  @Input() selectedItem?: Objection;
  @Input() isNewEditVisible = false;
  @Input() type: ObjectionType = ObjectionType.emotional;

  @Output() isNewEditVisibleChange = new EventEmitter<boolean>();

  @Input() isEdit = false;

  validateForm: FormRecord<FormControl<string>>;

  ObjectionType = ObjectionType;

  constructor(
    private objectionService: ObjectionService,
    private fb: NonNullableFormBuilder
  ) {
    this.validateForm = this.fb.group({
      title: ['', [Validators.required]],
      description: ['', [Validators.required]],
      type: [this.type, [Validators.required]],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName === 'selectedItem') {
        const chng = changes[propName];
        if (chng.previousValue !== chng.currentValue) {
          const selectedItem = chng.currentValue;

          this.validateForm.patchValue({
            title: selectedItem.title ?? '',
            description: selectedItem.description ?? '',
            type: selectedItem.type ?? ObjectionType.emotional,
          });
        }
      } else if (propName === 'type') {
        const chng = changes[propName];
        if (chng.previousValue !== chng.currentValue) {
          const type = chng.currentValue;

          this.validateForm.patchValue({
            type,
          });
        }
      }
    }
  }

  onIsNewEditVisibleChange(event: boolean) {
    this.isNewEditVisibleChange.emit(event);
  }

  onCancelClick() {
    this.isNewEditVisible = false;
    this.isNewEditVisibleChange.emit(false);
  }

  submitForm() {
    if (this.validateForm.valid) {
      this.isNewEditVisible = false;
      this.isNewEditVisibleChange.emit(false);

      const title = this.validateForm.value['title'];
      const description = this.validateForm.value['description'];
      const type = this.validateForm.value['type'] as ObjectionType;

      const subData = {};

      if (this.isEdit) {
        this.selectedItem!.title = title;
        this.selectedItem!.description = description;
        this.selectedItem!.type = type;

        this.selectedItem!.subData = subData;

        this.objectionService.update$(this.selectedItem!).pipe(take(1)).subscribe();
      } else {
        this.objectionService
          .addItem$(title ?? '', description ?? '', type, subData)
          .pipe(take(1))
          .subscribe(() => {
            this.validateForm = this.fb.group({
              title: ['', [Validators.required]],
              description: ['', [Validators.required]],
              type: [this.type, [Validators.required]],
            });
          });
      }
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }
}
